package com.example.mystybag.login.views

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.zIndex
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlin.math.min
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.native.CaptionCapitalization
import com.example.mystybag.communication.defines.CommunicationDefines.Companion.DOMAIN
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonSize
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.core.ui.theme.LocalTheme
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.login.logic.WelcomeViewModel
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource

private const val CLIENT_BACKGROUND = "images/welcome-client-bg.png"
private const val MERCHANT_BACKGROUND = "images/welcome-merchant-bg.png"
private val WELCOME_LOGO_MARK: String
    get() = "https://$DOMAIN/static/email_photos/logo_40.png"

@Composable
fun WelcomeViewWeb(viewModel: WelcomeViewModel) {
    WelcomePageScrollLock()

    val state = viewModel.uiState.subscribeAsState()
    val isClientTab = state.value.isClientTabSelected
    val colors = AppTheme.current.colors
    BoxWithConstraints(
        modifier = Modifier
            .fillMaxSize()
            .background(colors.surface)
    ) {
        val scale = min(maxWidth.value / 1920f, maxHeight.value / 1080f)
        WelcomeDesktopFixedLayout(
            viewModel = viewModel,
            isClientTab = isClientTab,
            scale = scale
        )
        Box(
            modifier = Modifier
                .align(Alignment.TopCenter)
                .fillMaxWidth()
                .zIndex(1f)
        ) {
            WelcomeDesktopHeaderActions(
                brandIconUrl = WELCOME_LOGO_MARK,
                brandText = "MystyBag",
                onHelpClick = viewModel::onHelpClick
            )
        }
    }
}

@Composable
private fun WelcomeDesktopFixedLayout(
    viewModel: WelcomeViewModel,
    isClientTab: Boolean,
    scale: Float
) {
    Row(modifier = Modifier.fillMaxSize()) {
        // LEFT HALF: form anchored at a fixed Y (no jump on tab switch)
        Box(
            modifier = Modifier
                .weight(1f)
                .fillMaxHeight()
        ) {
            val leftStart = (255f * scale).dp
            val formTop = (276f * scale).dp
            Box(
                modifier = Modifier
                    .padding(start = leftStart, top = formTop)
                    .width((450f * scale).dp)
            ) {
                WelcomeForm(
                    viewModel = viewModel,
                    isClientTab = isClientTab
                )
            }
        }

        // RIGHT HALF: solid black background (Compose). On top sits an HTML <img> that fills
        // the panel from edge to edge. The image carries a CSS mask with two cutouts:
        //   - bottom band → transparent (the Compose-drawn hero text shines through cleanly)
        //   - top-right corner → transparent (the Compose-drawn Help/EN header shines through)
        // Everything in the image's interior stays fully opaque, so the photo touches the
        // very top of the panel just like in Figma.
        Box(
            modifier = Modifier
                .weight(1f)
                .fillMaxHeight()
                .background(Color.Black)
        ) {
            WelcomeHeroText(
                isClientTab = isClientTab,
                modifier = Modifier
                    .align(Alignment.BottomStart)
                    .padding(
                        start = (100f * scale).dp,
                        end = (100f * scale).dp,
                        bottom = (80f * scale).dp
                    )
                    .widthIn(max = 450.dp)
            )

            WelcomeFadingImage(
                url = if (isClientTab) CLIENT_BACKGROUND else MERCHANT_BACKGROUND,
                modifier = Modifier
                    .fillMaxWidth()
                    .fillMaxHeight()
            )
        }
    }
}

@Composable
private fun WelcomeHeroText(
    isClientTab: Boolean,
    modifier: Modifier = Modifier
) {
    val title = if (isClientTab) "Вземи повече за по-малко" else "Остава ти непродадена храна?"
    val subtitle = if (isClientTab) {
        "Повече стойност, по-малко разходи - с всяка MystyBag."
    } else {
        "Превърни я в печалба с MystyBag – бързо и лесно."
    }
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(24.dp)
    ) {
        Text(
            text = title,
            color = Color.White,
            fontFamily = LocalTheme.googleSans,
            fontSize = 64.sp,
            lineHeight = 64.sp,
            fontWeight = FontWeight.Bold
        )
        Text(
            text = subtitle,
            color = Color.White,
            fontFamily = LocalTheme.googleSans,
            fontSize = 16.sp,
            lineHeight = 24.sp,
            letterSpacing = 0.5.sp,
            fontWeight = FontWeight.Normal
        )
    }
}

@Composable
private fun WelcomeForm(
    viewModel: WelcomeViewModel,
    isClientTab: Boolean,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(48.dp)
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(48.dp)) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(105.dp),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = stringResource(SR.strings.what_kind_of_user_are_you),
                    color = AppTheme.current.colors.onSurface,
                    fontFamily = LocalTheme.googleSans,
                    fontSize = 48.sp,
                    lineHeight = 52.8.sp,
                    fontWeight = FontWeight.Bold,
                    textAlign = TextAlign.Center
                )
            }
            WelcomeSegmentControl(
                isClientTab = isClientTab,
                onTabSelected = viewModel::onTabSelected
            )
        }

        if (isClientTab) {
            ClientActions(viewModel)
        } else {
            MerchantActions(viewModel)
        }
    }
}

@Composable
private fun WelcomeSegmentControl(
    isClientTab: Boolean,
    onTabSelected: (Boolean) -> Unit
) {
    val colors = AppTheme.current.colors
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(48.dp)
            .clip(RoundedCornerShape(200.dp))
            .background(colors.surfaceContainerLowest)
            .padding(4.dp)
    ) {
        BoxWithConstraints(
            modifier = Modifier
                .fillMaxWidth()
                .height(40.dp)
        ) {
            val segmentWidth = maxWidth / 2
            val tabIndex = if (isClientTab) 0f else 1f
            val fraction by animateFloatAsState(
                targetValue = tabIndex,
                animationSpec = tween(durationMillis = 250),
                label = "webSegmentIndicator"
            )
            val slidingOffsetDp = (segmentWidth.value * fraction).dp
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .offset(x = slidingOffsetDp)
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth(0.5f)
                        .fillMaxSize()
                        .clip(RoundedCornerShape(200.dp))
                        .background(colors.surface)
                )
            }
            Row(
                modifier = Modifier.fillMaxSize(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                listOf(
                    stringResource(SR.strings.client) to true,
                    stringResource(SR.strings.merchant) to false
                ).forEach { (label, isClient) ->
                    val selected = isClientTab == isClient
                    Box(
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxWidth()
                            .height(40.dp)
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null
                            ) { onTabSelected(isClient) },
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = label,
                            color = if (selected) colors.primary else colors.onSurfaceVariant,
                            fontFamily = LocalTheme.googleSans,
                            fontSize = 16.sp,
                            lineHeight = 24.sp,
                            letterSpacing = if (selected) 0.15.sp else 0.5.sp,
                            fontWeight = if (selected) FontWeight.Medium else FontWeight.Normal,
                            textAlign = TextAlign.Center
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ClientActions(viewModel: WelcomeViewModel) {
    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        DefaultButton(
            modifier = Modifier.fillMaxWidth(),
            type = DefaultButtonType.OUTLINED_SURFACE,
            caption = stringResource(SR.strings.continue_with_facebook),
            capitalization = CaptionCapitalization.None,
            icon = painterResource(SR.images.ic_facebook),
            height = 58.dp,
            callback = viewModel::onFacebookLogin,
            identifier = "Welcome Web Facebook"
        )
        DefaultButton(
            modifier = Modifier.fillMaxWidth(),
            type = DefaultButtonType.OUTLINED_SURFACE,
            caption = stringResource(SR.strings.continue_with_email),
            capitalization = CaptionCapitalization.None,
            icon = painterResource(SR.images.ic_mail),
            height = 58.dp,
            callback = viewModel::onEmailLogin,
            identifier = "Welcome Web Email"
        )
        DefaultButton(
            modifier = Modifier.fillMaxWidth(),
            type = DefaultButtonType.OUTLINED_SURFACE,
            caption = stringResource(SR.strings.continue_with_google),
            capitalization = CaptionCapitalization.None,
            icon = painterResource(SR.images.ic_google),
            height = 58.dp,
            callback = viewModel::onGoogleLogin,
            identifier = "Welcome Web Google"
        )
        DefaultButton(
            modifier = Modifier.fillMaxWidth(),
            type = DefaultButtonType.OUTLINED_SURFACE,
            caption = stringResource(SR.strings.continue_with_apple),
            capitalization = CaptionCapitalization.None,
            icon = painterResource(SR.images.ic_apple),
            height = 58.dp,
            callback = viewModel::onAppleLogin,
            identifier = "Welcome Web Apple"
        )
    }
}

@Composable
private fun MerchantActions(viewModel: WelcomeViewModel) {
    Column(verticalArrangement = Arrangement.spacedBy(20.dp)) {
        DefaultButton(
            modifier = Modifier.fillMaxWidth(),
            type = DefaultButtonType.PRIMARY,
            size = DefaultButtonSize.LARGE,
            caption = stringResource(SR.strings.login_as_merchant),
            capitalization = CaptionCapitalization.None,
            callback = viewModel::onMerchantLogin,
            identifier = "Welcome Web Merchant Login"
        )
        DefaultButton(
            modifier = Modifier.fillMaxWidth(),
            type = DefaultButtonType.SECONDARY,
            size = DefaultButtonSize.LARGE,
            caption = stringResource(SR.strings.login_as_staff),
            capitalization = CaptionCapitalization.None,
            callback = viewModel::onStaffLogin,
            identifier = "Welcome Web Staff Login"
        )
        Box(
            modifier = Modifier.fillMaxWidth(),
            contentAlignment = Alignment.Center
        ) {
            DefaultButton(
                modifier = Modifier.wrapContentWidth(),
                type = DefaultButtonType.TERTIARY,
                size = DefaultButtonSize.LARGE,
                caption = stringResource(SR.strings.continue_as_merchant),
                capitalization = CaptionCapitalization.None,
                callback = viewModel::onMerchantRegistration,
                identifier = "Welcome Web Merchant Registration"
            )
        }
    }
}
